/**
 * @fileoverview Icon component - Atomic UI element
 * 
 * A wrapper component for icons with consistent sizing and styling.
 * Built with Tailwind CSS.
 * 
 * @module ui/shared/atoms/Icon
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Icon variant definitions using CVA
 */
const iconVariants = cva("inline-flex items-center justify-center", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      default: "h-5 w-5",
      lg: "h-6 w-6",
      xl: "h-8 w-8",
      "2xl": "h-12 w-12",
    },
    iconColor: {
      default: "text-gray-600",
      primary: "text-primary-600",
      success: "text-green-600",
      warning: "text-yellow-600",
      danger: "text-red-600",
      muted: "text-gray-400",
      white: "text-white",
    },
  },
  defaultVariants: {
    size: "default",
    iconColor: "default",
  },
});

/**
 * Icon component props
 */
export interface IconProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof iconVariants> {
  /**
   * Icon element (from @/ui/compat/icons or another icon library)
   */
  children: React.ReactNode;
  /**
   * Icon color (renamed from color to avoid conflict with HTML color attribute)
   */
  color?: VariantProps<typeof iconVariants>["iconColor"];
}

/**
 * Icon component
 * 
 * A wrapper for icons with consistent sizing and styling.
 * 
 * @param {IconProps} props - Icon props
 * @returns {JSX.Element} Icon wrapper element
 * 
 * @example
 * <Icon size="lg" color="primary">
 *   <DollarOutlined />
 * </Icon>
 */
const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  ({ className, size, color, iconColor, children, ...props }, ref) => {
    // Use color prop if provided, otherwise use iconColor
    const finalIconColor = color || iconColor;
    return (
      <span
        ref={ref}
        className={cn(iconVariants({ size, iconColor: finalIconColor }), className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Icon.displayName = "Icon";

export { Icon, iconVariants };
